/**
 * The lead drawer's write-back line: for a client whose quotable mark Core
 * owns, whether Core wrote the mark to WhatConverts (and when), or why it
 * did not. For a client the Planner still owns there is nothing to say, so
 * the drawer keeps its "kept in Core only" note.
 */
import type { WcLeadWriteback, WcQuotable } from '@/services/whatconvertsService';

import { LABEL_TEXT } from './leadLabels';

const WC_TEXT: Record<WcQuotable, string> = { ...LABEL_TEXT, not_set: 'Not set' };

function at(iso: string | null | undefined): string {
  if (!iso) return '';
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return '';
  return ` on ${t.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`;
}

function skipText(reason: string | null, before: WcQuotable | null): string {
  switch (reason) {
    case 'wc_already_marked':
      return before
        ? `someone had already marked it "${WC_TEXT[before] ?? before}" in WhatConverts`
        : 'someone had already marked it in WhatConverts';
    case 'too_old':
      return 'the lead is older than the write window';
    case 'no_label':
      return 'Core has no label for it yet';
    case 'reread_failed':
      return 'WhatConverts could not be read just before writing, so Core will try again';
    case 'profile_mismatch':
      return 'WhatConverts shows it under a different profile';
    case 'still_pending':
      return 'the label is still Pending, so Core is leaving its own Pending mark as it is';
    case 'wc_changed_since_pending':
      return 'the mark was changed in WhatConverts after Core wrote Pending, so Core leaves it';
    case 'modified_after_core_pending':
      return 'someone edited the lead in WhatConverts after Core wrote Pending, so Core leaves it';
    default:
      return 'see the write log';
  }
}

export interface WritebackSummary {
  /** One sentence about this lead. */
  line: string;
  /** Replaces the drawer's footer note for this client. */
  footer: string;
}

export function writebackSummary(wb: WcLeadWriteback | null | undefined): WritebackSummary | null {
  if (!wb || wb.effective === 'planner') return null;
  const live = wb.effective === 'core_live';
  const footer = live
    ? 'Core writes the quotable mark to WhatConverts for this client. Team corrections stay in Core.'
    : 'Core is testing the quotable write for this client. Nothing is sent to WhatConverts yet.';
  const w = wb.latest;
  if (!w) {
    return {
      line: live
        ? 'Core has not written a mark for this lead yet.'
        : 'Core has not checked this lead yet.',
      footer,
    };
  }
  const label = w.after_quotable ? `"${LABEL_TEXT[w.after_quotable]}"` : 'a mark';
  const when = at(w.finished_at ?? w.created_at);
  switch (w.status) {
    case 'success':
      return { line: `Core wrote ${label} to WhatConverts${when}.`, footer };
    case 'dry_run':
      return { line: `Test mode: Core would have written ${label}${when}. Nothing was sent.`, footer };
    case 'pending':
      return { line: 'Core is writing this mark now.', footer };
    case 'error':
      return { line: `Core tried to write ${label}${when}, and WhatConverts refused. Core will try again.`, footer };
    default:
      return { line: `Core did not write${when}: ${skipText(w.reason, w.before_quotable)}.`, footer };
  }
}
